import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { ApiResult } from 'src/Libs/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { AuthFetchWithResponse } from 'src/Types/api'
import { createEvse, deleteEvse, ControlledDto, ControlledPOSTDto } from 'src/Libs/flow/installation/api'
import { updateEvse, ControlledModbusTcpPUTDto } from 'src/Libs/flow/end-user-settings/wallbox/api/evse.api'
import { getSunMode } from 'src/Libs/flow/parsers/device-parsers/evse/sun-mode'
import { PlannedWallboxModel, WallboxForm } from 'src/Libs/installations/models'
import { WALLBOX_MODEL_TYPE_CONFIG } from 'src/Libs/installations/utils/wallbox-model-type'

const toWallboxApiResult = <T>(result: ApiResult<T>): ApiResult<T, AuthFetchError | Error> =>
  new ApiResult<T, AuthFetchError | Error>(result.value, result.hasErrors, result.error as AuthFetchError | Error | undefined)

/**
 * Sensible defaults for the required backend parameters that are not (yet) exposed in the simple
 * wallbox configuration UI. Sun mode charging is kept disabled by default via the shared getSunMode
 * helper used by the other Evse parsers.
 */
const DEFAULT_WALLBOX_PARAMETERS: NonNullable<ControlledPOSTDto['parameters']> = {
  priority: 100,
  authenticationMode: 'freeAccess',
  phasesManagement: 'AutoPhaseSwitch',
  activated: true,
  minCurrent: 6,
  minCurrentSunMode: 6,
  fallbackMaxCurrent: 16,
  chargeInFallbackModeAllowed: true,
  protection: 16,
  ledIntensity: 100,
  useMidMeter: false,
  pulseWeight: 0,
  sunMode: getSunMode(0, 'standard', [], []),
  phaseMapping: '123',
}

/**
 * The backend may return `parameterList: null` for a wallbox with `chargingStrategy: 'Disabled'` (see GET
 * response), but the POST/PUT schema requires an array (length 0 for 'Disabled', exactly 7 otherwise).
 * Sanitizes a sun mode object fetched from the backend so it can be safely echoed back in a PUT payload.
 */
const sanitizeSunModeForPayload = (
  sunMode: NonNullable<ControlledDto['parameters']>['sunMode'] | undefined
): NonNullable<ControlledPOSTDto['parameters']>['sunMode'] => {
  if (!sunMode?.activated || !sunMode?.chargingStrategy || !sunMode?.holdingTimeInMin) {
    return DEFAULT_WALLBOX_PARAMETERS.sunMode
  }

  return {
    activated: sunMode.activated,
    chargingStrategy: sunMode.chargingStrategy,
    holdingTimeInMin: sunMode.holdingTimeInMin,
    parameterList: sunMode.parameterList ?? [],
  }
}

const buildWallboxCreatePayload = (form: WallboxForm): ControlledPOSTDto => {
  const config = WALLBOX_MODEL_TYPE_CONFIG[form.modelType]

  return {
    deviceName: form.name,
    type: 'Evse',
    media: config.media,
    parameters: DEFAULT_WALLBOX_PARAMETERS,
    evseSubTypeParameters: {
      wallboxId: null,
      wbType: config.wbType,
      brand: config.brand,
      keyLockMode: config.keyLockMode,
    },
  }
}

const buildWallboxUpdatePayload = (form: WallboxForm, wallbox: PlannedWallboxModel): ControlledModbusTcpPUTDto => {
  const config = WALLBOX_MODEL_TYPE_CONFIG[form.modelType]
  const existingParameters = wallbox.parameters as Partial<NonNullable<ControlledDto['parameters']>>
  const existingEvseSubTypeParameters = wallbox.evseSubTypeParameters as Partial<NonNullable<ControlledDto['evseSubTypeParameters']>>

  return {
    deviceName: form.name,
    parameters: {
      protection: existingParameters.protection ?? DEFAULT_WALLBOX_PARAMETERS.protection,
      priority: existingParameters.priority ?? DEFAULT_WALLBOX_PARAMETERS.priority,
      activated: existingParameters.activated ?? DEFAULT_WALLBOX_PARAMETERS.activated,
      useMidMeter: existingParameters.useMidMeter ?? DEFAULT_WALLBOX_PARAMETERS.useMidMeter,
      pulseWeight: existingParameters.pulseWeight ?? DEFAULT_WALLBOX_PARAMETERS.pulseWeight,
      sunMode: sanitizeSunModeForPayload(existingParameters.sunMode),
      authenticationMode: existingParameters.authenticationMode ?? DEFAULT_WALLBOX_PARAMETERS.authenticationMode,
      phasesManagement: existingParameters.phasesManagement ?? DEFAULT_WALLBOX_PARAMETERS.phasesManagement,
      minCurrent: existingParameters.minCurrent ?? DEFAULT_WALLBOX_PARAMETERS.minCurrent,
      chargeInFallbackModeAllowed: existingParameters.chargeInFallbackModeAllowed ?? DEFAULT_WALLBOX_PARAMETERS.chargeInFallbackModeAllowed,
      fallbackMaxCurrent: existingParameters.fallbackMaxCurrent ?? DEFAULT_WALLBOX_PARAMETERS.fallbackMaxCurrent,
      minCurrentSunMode: existingParameters.minCurrentSunMode ?? DEFAULT_WALLBOX_PARAMETERS.minCurrentSunMode,
      ledIntensity: existingParameters.ledIntensity ?? DEFAULT_WALLBOX_PARAMETERS.ledIntensity,
      lockCable: existingParameters.lockCable ?? false,
      phaseMapping: existingParameters.phaseMapping ?? DEFAULT_WALLBOX_PARAMETERS.phaseMapping,
    },
    evseSubTypeParameters: {
      ocppActivation: existingEvseSubTypeParameters.ocppActivation ?? null,
      ocppServerAddress: existingEvseSubTypeParameters.ocppServerAddress ?? null,
      ocppId: existingEvseSubTypeParameters.ocppId ?? null,
      ocppAuthType: existingEvseSubTypeParameters.ocppAuthType ?? null,
      deviceReference: existingEvseSubTypeParameters.deviceReference ?? null,
      wbType: config.wbType,
      keyLockMode: config.keyLockMode ?? null,
    },
  }
}

export const createPlannedWallbox = async (
  installationId: number,
  emcDeviceLinkId: string,
  form: WallboxForm,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<string | null, AuthFetchError | Error>> => {
  try {
    const result = await createEvse(installationId, emcDeviceLinkId, buildWallboxCreatePayload(form), authFetch)
    return toWallboxApiResult(result)
  } catch (error) {
    return handleFetchError<string | null>(error)
  }
}

export const updatePlannedWallbox = async (
  installationId: number,
  emcDeviceLinkId: string,
  wallbox: PlannedWallboxModel,
  form: WallboxForm,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null, AuthFetchError | Error>> => {
  try {
    const result = await updateEvse(installationId, emcDeviceLinkId, wallbox.id.toString(), buildWallboxUpdatePayload(form, wallbox), authFetch)
    return toWallboxApiResult(result)
  } catch (error) {
    return handleFetchError<null>(error)
  }
}

export const deletePlannedWallbox = async (
  installationId: number,
  emcDeviceLinkId: string,
  wallbox: PlannedWallboxModel,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null, AuthFetchError | Error>> => {
  try {
    const result = await deleteEvse(installationId, emcDeviceLinkId, wallbox.id.toString(), authFetch)
    return toWallboxApiResult(result)
  } catch (error) {
    return handleFetchError<null>(error)
  }
}
